import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { SyntheticEvent } from 'react';
import { Autocomplete, Chip, TextField } from '@mui/material';
import { X } from 'lucide-react';
import { MAX_PRODUCT_GROUP_TYPES, moveType } from './hub-product-group-types';

export interface ProductGroupTypeOption {
  id: string;
  name?: string | null;
}

interface ProductGroupTypeMultiSelectProps {
  /** 選択中の ID（先頭 = 既定タイプ。並び順 = Hub の表示順） */
  value: string[];
  onChange: (ids: string[]) => void;
  types: ProductGroupTypeOption[];
}

interface SortableTypeChipProps {
  id: string;
  label: string;
  isDefault: boolean;
  onDelete: (event: SyntheticEvent) => void;
}

function SortableTypeChip({ id, label, isDefault, onDelete }: SortableTypeChipProps) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  return (
    <Chip
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      size="small"
      label={isDefault ? `既定: ${label}` : label}
      color={isDefault ? 'primary' : 'default'}
      onDelete={onDelete}
      deleteIcon={<X size={14} aria-label={`${label}を削除`} />}
      sx={{ cursor: 'grab', touchAction: 'none' }}
      {...attributes}
      {...listeners}
    />
  );
}

/**
 * 商品グループタイプの複数選択（チップをドラッグ / キーボードで並べ替え）。
 * 先頭のチップが既定タイプで、2 つ目以降が Hub で切り替えて使うタイプ。合計の上限は 5。
 */
export function ProductGroupTypeMultiSelect({
  value,
  onChange,
  types,
}: ProductGroupTypeMultiSelectProps) {
  const sensors = useSensors(
    // 距離しきい値を置かないと、チップの削除ボタンのクリックがドラッグ開始と競合する
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const byId = new Map(types.map((t) => [t.id, t]));
  const selected = value.map((id) => byId.get(id) ?? { id, name: null });
  const nameOf = (o: ProductGroupTypeOption) => o.name || o.id;

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over) return;
    onChange(moveType(value, String(active.id), String(over.id)));
  };

  return (
    <Autocomplete<ProductGroupTypeOption, true>
      multiple
      fullWidth
      disableCloseOnSelect
      options={types}
      value={selected}
      getOptionLabel={nameOf}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionDisabled={(o) => !value.includes(o.id) && value.length >= MAX_PRODUCT_GROUP_TYPES}
      onChange={(_, next) => onChange(next.map((o) => o.id))}
      noOptionsText="選択できる商品グループタイプがありません"
      renderValue={(items, getItemProps) => (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToHorizontalAxis]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={value} strategy={horizontalListSortingStrategy}>
            {items.map((item, index) => {
              const { key, onDelete } = getItemProps({ index });
              return (
                <SortableTypeChip
                  key={key}
                  id={item.id}
                  label={nameOf(item)}
                  isDefault={index === 0}
                  onDelete={onDelete}
                />
              );
            })}
          </SortableContext>
        </DndContext>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="商品グループタイプ"
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
          helperText="先頭が既定のタイプです。2つ目以降は Hub で切り替えて使えます（最大5つ）。"
        />
      )}
    />
  );
}
